import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Play, CheckCircle } from 'lucide-react';
import AnimatedSection from './AnimatedSection';
import StaggeredList from './StaggeredList';

const HeroSectionSandbox = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const handleTrialRequest = () => {
    window.location.href = '/trial-request';
  };

  const handleSampleRequest = () => {
    window.location.href = '/sample-request';
  };

  const handleWatchDemo = () => {
    const videoSection = document.getElementById('video-section');
    if (videoSection) {
      videoSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const features = [];// ['Single-Use', 'FDA Registered'];
  const trustIndicators = [
    'Developed by ICU Nurses & Biomedical Engineers',
    '90% of surveyed nurses would use Cordflex® if stocked'
  ];

  const heroImages = [
    {
      src: '/images/products/cordflexBedrail_handsTracingIVs.jpeg',
      alt: 'Hands Line Tracing Cordflex on Bedrail'
    },
    {
      src: '/images/nurses-patients/nurse-and-patient1.png',
      alt: 'Cordflex clip holding many lines in ICU'
    },
    {
      src: '/images/products/White Cordflex on Bedrail - IV Lines & Cords 41.jpeg',
      alt: 'Cordflex on Bedrail with IV Lines'
    },
    {
      src: '/images/products/GreenCordflexYoungNurseHolding.png',
      alt: 'Nurse holding Cordflex Clip'
    },

    {
      src: '/images/products/bedrailCordflex_manyLines.jpeg',
      alt: 'Cordflex clip holding many lines in ICU'
    }
  ];

  // Auto-rotate images every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative pt-32 pb-24 overflow-hidden bg-white">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <svg className="w-full h-full" viewBox="0 0 100 100">
          <defs>
            {/* <pattern id="hero-pattern" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
              <circle cx="10" cy="10" r="1.5" fill="#214140" />
            </pattern> */}
          </defs>
          <rect width="100%" height="100%" fill="url(#hero-pattern)" />
        </svg>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Content */}
          <div className="space-y-8">
            {/* Trust Badges */}
            <AnimatedSection animation="fade-up" delay={100}>
              <StaggeredList delay={50} className="flex flex-wrap gap-2">
                {features.map((feature, index) => (
                  <Badge
                    key={index}
                    variant="secondary"
                    className="text-sm font-medium"
                    style={{ backgroundColor: '#DFEAF0', color: '#214140' }}
                  >
                    {feature}
                  </Badge>
                ))}
              </StaggeredList>
            </AnimatedSection>

            {/* Headline */}
            <AnimatedSection animation="fade-up" delay={200}>
              <div className="space-y-4">
                <h1 className="text-4xl lg:text-6xl font-bold leading-tight" style={{ color: '#214140' }}>
                  Transform ICU Line Management in &lt;30 seconds
                </h1>
                <p className="text-xl leading-relaxed text-gray-700">
                  Designed to keep ICU lines organized. 74% of surveyed nurses said they would feel safer ambulating patients with Cordflex, and line-organizing devices have been shown to help staff arrange IV lines for patient transport 37% faster (Joo et al., 2016).
                </p>
              </div>
            </AnimatedSection>

            {/* Trust Indicators */}
            <AnimatedSection animation="fade-up" delay={300}>
              <StaggeredList delay={100} className="space-y-3">
                {trustIndicators.map((indicator, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 flex-shrink-0" style={{ color: '#1F8051' }} />
                    <span className="text-gray-700 font-medium">{indicator}</span>
                  </div>
                ))}
              </StaggeredList>
            </AnimatedSection>

            {/* CTA Buttons */}
            <AnimatedSection animation="fade-up" delay={400}>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={handleTrialRequest}
                  size="lg"
                  className="px-8 py-4 text-lg font-semibold hover:opacity-90 transition-all duration-200 transform hover:scale-105"
                  style={{ backgroundColor: '#1F8051', color: 'white' }}
                >
                  Request a Trial
                </Button>
                <Button
                  onClick={handleSampleRequest}
                  variant="outline"
                  size="lg"
                  className="px-8 py-4 text-lg font-semibold border-2 hover:bg-gray-50 transition-all duration-200 transform hover:scale-105"
                  style={{ borderColor: '#214140', color: '#214140' }}
                >
                  Request a Sample
                </Button>
              </div>
            </AnimatedSection>

            {/* Watch Demo Link */}
            <AnimatedSection animation="fade-up" delay={500}>
              <button
                onClick={handleWatchDemo}
                className="flex items-center space-x-2 text-gray-700 hover:text-gray-900 transition-colors duration-200 group"
              >
                <Play className="h-5 w-5 group-hover:scale-110 transition-transform duration-200" style={{ color: '#1F8051' }} />
                <span className="underline">Watch Demo</span>
              </button>
            </AnimatedSection>
          </div>

          {/* Right Column - Product Images */}
          <AnimatedSection animation="fade-left" delay={300}>
            <div className="relative">
              {/* Main Image Display with Fade Transition */}
              <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-[4/5]">
                {heroImages.map((image, index) => (
                  <img
                    key={index}
                    src={image.src}
                    alt={image.alt}
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
                      index === currentImageIndex ? 'opacity-100' : 'opacity-0'
                    }`}
                  />
                ))}
              </div>

              {/* Image Indicators */}
              <div className="flex justify-center space-x-2 mt-6">
                {heroImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      index === currentImageIndex
                        ? 'w-8'
                        : 'hover:scale-110'
                    }`}
                    style={{
                      backgroundColor: index === currentImageIndex ? '#1F8051' : '#DFEAF0'
                    }}
                    aria-label={`View image ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

export default HeroSectionSandbox;
